import type { ReactNode } from 'react';

import { Reveal } from '@/components/public/Reveal';
import { cn } from '@/lib/utils';

/*
 * The public site's layout primitives — one container, used everywhere.
 *
 * design-system.md §6 makes the same point about the admin shell: "every admin
 * page uses the identical container", because the Expenses page had drifted
 * wider than the rest and nobody could say why. Same reasoning here, before it
 * has a chance to happen.
 *
 * **On centring.** The rule these components encode is: *a heading is aligned
 * the way the thing it introduces is aligned.* A heading over a symmetrical row
 * of cards is centred, because a left-flush heading above three evenly-spaced
 * cards leaves an obvious dead corner and reads as unfinished. A heading beside
 * a photograph, or over prose somebody has to read, stays left — centred body
 * text has a ragged left edge and is measurably harder to read.
 */

export type Align = 'left' | 'center';

export function Section({
  children,
  className,
  tint,
  labelledBy,
}: {
  children: ReactNode;
  className?: string;
  /** A quiet band. `--ink-50` or `--honey-50`, never a saturated fill. */
  tint?: 'none' | 'ink' | 'honey';
  labelledBy?: string;
}) {
  return (
    <section
      aria-labelledby={labelledBy}
      className={cn(
        // Roomier than it was. Marketing pages breathe; the density that suits
        // an admin table makes a landing page feel cramped and busy.
        'px-4 py-16 sm:px-6 sm:py-24',
        tint === 'ink' && 'bg-ink-50',
        tint === 'honey' && 'bg-honey-50',
        className,
      )}
    >
      <div className="mx-auto max-w-content">{children}</div>
    </section>
  );
}

/**
 * The block above a section's content — eyebrow, heading, lede.
 *
 * One component rather than three loose ones, so alignment is decided once per
 * section instead of three times. Getting the eyebrow centred and the lede left
 * is the kind of near-miss nobody spots and everybody feels.
 */
export function SectionIntro({
  eyebrow,
  heading,
  lede,
  id,
  align = 'left',
}: {
  eyebrow?: ReactNode;
  heading: ReactNode;
  lede?: ReactNode;
  id?: string;
  align?: Align;
}) {
  const centered = align === 'center';

  return (
    <Reveal className={cn(centered && 'text-center')}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <SectionHeading id={id} className={cn(eyebrow ? 'mt-3' : undefined)}>
        {heading}
      </SectionHeading>
      {/* `mx-auto` on the lede as well, or a centred heading sits above a
          left-flush paragraph and the whole block looks accidental. */}
      {lede ? <Lede className={cn(centered && 'mx-auto')}>{lede}</Lede> : null}
    </Reveal>
  );
}

/** The small uppercase line above a heading. `OUR WORK`, `AWARD-WINNING SERVICE`. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-bark-600">
      {children}
    </p>
  );
}

/**
 * A section heading.
 *
 * The display scale — design-system.md §4: "The marketing pages may run larger
 * (hero at 48–60px) — that is the one place a display scale is appropriate."
 *
 * `text-balance` so a two-line heading breaks evenly instead of leaving one
 * orphaned word on the second line. It is one CSS property and it is the
 * difference between a heading that looks set and one that looks wrapped.
 */
export function SectionHeading({
  children,
  id,
  className,
}: {
  children: ReactNode;
  id?: string;
  className?: string;
}) {
  return (
    <h2
      id={id}
      className={cn(
        'text-balance text-[30px] font-bold leading-[1.15] tracking-tight sm:text-[38px]',
        className,
      )}
    >
      {children}
    </h2>
  );
}

export function Lede({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn('mt-4 max-w-2xl text-[17px] leading-relaxed text-ink-700', className)}>
      {children}
    </p>
  );
}

/**
 * A flat, quiet card that lifts when you point at it.
 *
 * design-system.md §5 asks for flat and quiet — "the prototype does not use
 * heavy shadows and should not start" — and that is about the *resting* state.
 * A card that responds to the cursor is not a heavy card; it is the difference
 * between a page that feels printed and one that feels like software.
 *
 * `motion-safe:` on the movement, so the lift is colour and shadow only for
 * anybody who has asked for less motion. The border warming to `honey-300` is
 * what carries the interaction for them.
 */
export function Card({
  children,
  className,
  interactive = true,
}: {
  children: ReactNode;
  className?: string;
  /** Off for a card that is a container rather than a thing — a form panel. */
  interactive?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-ink-300 bg-white p-6 shadow-card',
        interactive &&
          'transition-[box-shadow,border-color,transform] duration-300 hover:border-honey-300 hover:shadow-pop motion-safe:hover:-translate-y-1',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * A photograph in a frame, that eases in when pointed at.
 *
 * The zoom is on the image and the rounding is on the wrapper, so the corners
 * stay clipped while it scales — doing both on one element makes the corners
 * pop square for the length of the transition.
 */
export function Photo({
  src,
  alt,
  className,
  imageClassName,
  priority = false,
}: {
  src: string;
  alt: string;
  className?: string;
  imageClassName?: string;
  /** The one above the fold. Everything else waits until it is scrolled to. */
  priority?: boolean;
}) {
  return (
    <div
      className={cn(
        'group overflow-hidden rounded-xl border border-ink-300 shadow-card',
        'transition-shadow duration-500 hover:shadow-pop',
        className,
      )}
    >
      <img
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        {...(priority ? { fetchPriority: 'high' as const } : {})}
        className={cn(
          'h-full w-full object-cover',
          'transition-transform duration-700 ease-out motion-safe:group-hover:scale-[1.03]',
          imageClassName,
        )}
      />
    </div>
  );
}
